import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Clock } from "@phosphor-icons/react";
import {
  blogCategories,
  getCategoryBySlug,
  getCategoryMetadata,
  getCategorySlug,
  getPostsByCategory,
} from "@/data/blogData";
import SEO, { breadcrumbSchema } from "@/components/SEO";

function buildBlogCollectionSchema(posts, category, description, path) {
  const name = category === "All"
    ? "BitGenix Crypto Insights Blog"
    : `${category} Crypto Articles | BitGenix`;

  return {
    "@context": "https://schema.org",
    "@type": category === "All" ? "Blog" : "CollectionPage",
    name,
    description,
    url: `https://bitgenix.io${path}`,
    inLanguage: "en-IN",
    isPartOf: {
      "@type": "Blog",
      name: "BitGenix Crypto Insights",
      url: "https://bitgenix.io/blog",
    },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: posts.map((post, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: post.title,
        url: `https://bitgenix.io/blog/${post.slug}`,
      })),
    },
  };
}

function categoryPath(category) {
  return category === "All" ? "/blog" : `/blog/category/${getCategorySlug(category)}`;
}

export default function Blog() {
  const { categorySlug } = useParams();
  const activeCategory = categorySlug ? getCategoryBySlug(categorySlug) : "All";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categorySlug]);

  if (categorySlug && !activeCategory) {
    return (
      <div className="bg-[#F5F5F2] min-h-[70vh] flex items-center justify-center px-6">
        <SEO
          title="Blog Category Not Found"
          description="The BitGenix blog category you requested could not be found."
          path={`/blog/category/${categorySlug}`}
          robots="noindex, nofollow"
        />
        <div className="max-w-2xl text-center">
          <p className="font-body text-sm uppercase tracking-[0.2em] text-[#A7A55C] mb-5">Insights</p>
          <h1 className="font-heading font-semibold text-4xl text-[#21324D] leading-tight mb-6">
            This blog category does not exist
          </h1>
          <p className="font-body text-base text-[#383A46]/75 leading-relaxed mb-8">
            Head back to the main insights hub to browse BitGenix articles on crypto tax, compliance, investing, and blockchain strategy.
          </p>
          <Link
            to="/blog"
            className="bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-colors font-body font-semibold text-sm px-8 py-4 inline-flex items-center gap-2"
          >
            Back to Blog <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    );
  }

  const categoryMeta = getCategoryMetadata(activeCategory);
  const filteredPosts = getPostsByCategory(activeCategory);
  const featuredPost = filteredPosts[0] || null;
  const gridPosts = featuredPost
    ? filteredPosts.filter((post) => post.slug !== featuredPost.slug)
    : filteredPosts;
  const pagePath = activeCategory === "All" ? "/blog" : `/blog/category/${categorySlug}`;
  const breadcrumbItems = [
    { name: "Home", path: "/" },
    { name: "Blog", path: "/blog" },
  ];

  if (activeCategory !== "All") {
    breadcrumbItems.push({ name: activeCategory, path: pagePath });
  }

  const pageTitle = activeCategory === "All"
    ? categoryMeta.title
    : `${categoryMeta.title} | BitGenix`;

  return (
    <div data-testid="blog-page">
      <SEO
        title={pageTitle}
        description={categoryMeta.description}
        path={pagePath}
        keywords={categoryMeta.keywords}
        includeFeed
        jsonLd={[
          buildBlogCollectionSchema(filteredPosts, activeCategory, categoryMeta.description, pagePath),
          breadcrumbSchema(breadcrumbItems),
        ]}
      />

      <section className="bg-[#21324D] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <p className="font-body text-sm uppercase tracking-[0.2em] text-[#A7A55C] mb-6">
              {activeCategory === "All" ? "Insights" : activeCategory}
            </p>
            <h1
              className="font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl text-[#F5F5F2] leading-none tracking-tight mb-6"
              data-testid="blog-heading"
            >
              {categoryMeta.heroTitle}
            </h1>
            <p className="font-body text-lg text-[#F5F5F2]/80 leading-relaxed">
              {categoryMeta.heroDescription}
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white border-b border-[#383A46]/10 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <nav className="flex items-center gap-1 overflow-x-auto py-4 scrollbar-hide" aria-label="Blog categories" data-testid="blog-categories">
            {blogCategories.map((category) => {
              const isActive = activeCategory === category;
              return (
                <Link
                  key={category}
                  to={categoryPath(category)}
                  aria-current={isActive ? "page" : undefined}
                  className={`whitespace-nowrap px-4 py-2 font-body text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-[#21324D] text-[#F5F5F2]"
                      : "text-[#383A46] hover:text-[#A7A55C] hover:bg-[#F5F5F2]"
                  }`}
                  data-testid={`blog-category-${category.toLowerCase().replace(/\s/g, "-").replace(/&/g, "and")}`}
                >
                  {category}
                </Link>
              );
            })}
          </nav>
        </div>
      </section>

      {featuredPost && (
        <section className="py-16 sm:py-20 bg-[#F5F5F2]">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="flex items-center justify-between gap-6 mb-8">
              <div>
                <p className="font-body text-sm uppercase tracking-[0.15em] text-[#383A46]/50">
                  {activeCategory === "All" ? "Featured Article" : "Top Article in This Topic"}
                </p>
                <h2 className="font-heading font-semibold text-2xl sm:text-3xl text-[#21324D] mt-2">
                  {activeCategory === "All" ? "Start with the latest flagship guide" : `${activeCategory} articles worth starting with`}
                </h2>
              </div>
              <p className="font-body text-sm text-[#383A46]/60 max-w-md">
                {filteredPosts.length} article{filteredPosts.length === 1 ? "" : "s"} in this collection.
              </p>
            </div>

            <Link
              to={`/blog/${featuredPost.slug}`}
              className="group grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
              data-testid="featured-blog-post"
            >
              <div className="bg-[#21324D] aspect-[16/10] flex items-center justify-center p-12">
                <div>
                  <span className="font-body text-xs uppercase tracking-[0.15em] text-[#A7A55C] font-medium">
                    {featuredPost.category}
                  </span>
                  <h3 className="font-heading font-semibold text-2xl sm:text-3xl text-[#F5F5F2] mt-4 leading-tight">
                    {featuredPost.title}
                  </h3>
                </div>
              </div>
              <article>
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-body text-xs uppercase tracking-[0.15em] text-[#A7A55C] font-medium bg-[#A7A55C]/10 px-3 py-1">
                    {featuredPost.category}
                  </span>
                  <span className="font-body text-sm text-[#383A46]/60 flex items-center gap-1.5">
                    <Clock size={14} /> {featuredPost.readTime}
                  </span>
                </div>
                <h3 className="font-heading font-semibold text-2xl text-[#21324D] mb-4 group-hover:text-[#A7A55C] transition-colors leading-tight">
                  {featuredPost.title}
                </h3>
                <p className="font-body text-base text-[#383A46] leading-relaxed mb-6">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <span className="font-body text-sm text-[#383A46]/60">{featuredPost.date}</span>
                  <span className="font-body font-semibold text-sm text-[#A7A55C] inline-flex items-center gap-1.5 group-hover:gap-3 transition-all">
                    Read article <ArrowRight size={14} weight="bold" />
                  </span>
                </div>
              </article>
            </Link>
          </div>
        </section>
      )}

      <section className="py-16 sm:py-20 bg-[#F5F5F2]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <h2 className="font-body font-medium text-sm uppercase tracking-[0.15em] text-[#383A46]/50 mb-3">
                {activeCategory === "All" ? "All Articles" : `${activeCategory} Articles`}
              </h2>
              <p className="font-body text-base text-[#383A46]/70 max-w-2xl">
                {categoryMeta.description}
              </p>
            </div>
            {activeCategory !== "All" && (
              <Link
                to="/blog"
                className="font-body font-semibold text-sm text-[#A7A55C] inline-flex items-center gap-2"
              >
                Browse every category <ArrowRight size={14} weight="bold" />
              </Link>
            )}
          </div>

          {gridPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {gridPosts.map((post) => (
                <Link
                  to={`/blog/${post.slug}`}
                  key={post.slug}
                  className="group bg-white border border-[#383A46]/10 transition-all duration-300 hover:-translate-y-1 hover:border-[#A7A55C] hover:shadow-[0_12px_40px_-12px_rgba(33,50,77,0.1)]"
                  data-testid={`blog-card-${post.slug}`}
                >
                  <article>
                    <div className="bg-[#152F2C] h-48 flex items-end p-6">
                      <span className="font-body text-xs uppercase tracking-[0.15em] text-[#A7A55C] font-medium">
                        {post.category}
                      </span>
                    </div>
                    <div className="p-6 sm:p-8">
                      <h3 className="font-body font-medium text-lg text-[#21324D] mb-3 group-hover:text-[#A7A55C] transition-colors leading-snug">
                        {post.title}
                      </h3>
                      <p className="font-body text-sm text-[#383A46] leading-relaxed mb-6 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="font-body text-xs text-[#383A46]/50">{post.date}</span>
                          <span className="font-body text-xs text-[#383A46]/50 flex items-center gap-1">
                            <Clock size={12} /> {post.readTime}
                          </span>
                        </div>
                        <ArrowRight size={14} weight="bold" className="text-[#A7A55C] group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-white border border-[#383A46]/10">
              <p className="font-body text-base text-[#383A46]/60">No articles in this category yet.</p>
            </div>
          )}
        </div>
      </section>

      <section className="py-20 sm:py-24 bg-[#21324D]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#F5F5F2] tracking-tight leading-tight mb-6">
            Need expert crypto advisory?
          </h2>
          <p className="font-body text-base text-[#F5F5F2]/70 max-w-xl mx-auto mb-10">
            BitGenix helps individuals and businesses with crypto tax planning, compliance, investment strategy, and blockchain consulting.
          </p>
          <Link
            to="/contact"
            className="bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-colors duration-300 font-body font-semibold text-sm tracking-wide px-8 py-4 rounded-none inline-flex items-center gap-2"
            data-testid="blog-cta-button"
          >
            Book a Free Consultation <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
